import { omit } from "lodash";

import { Docs } from "~/bff/types/Docs";
import {
  IFullOverridePayload,
  IOnCompletedPayload,
  IUpdateDirection,
  IUpdateFilterOnClick,
  IUpdateIsShopFilter,
  IUpdateSorting,
  IUseRequestHandlerActions,
  IUseRequestHandlerActionsKind,
} from "~/types/products-request-handler";

import { IUseRequestHandler } from "../types";

export const requestReducer = (
  state: IUseRequestHandler,
  actions: IUseRequestHandlerActions,
): IUseRequestHandler => {
  const { type, payload } = actions;
  switch (type) {
    case IUseRequestHandlerActionsKind.UPDATE_ON_COMPLETED: {
      const {
        filters,
        currentPage,
        itemsMap,
        loadedItems,
        newItems,
        numFound,
        direction,
        shouldConcat,
        setNumFound,
        setShouldTriggerGAFilterEvent,
        isUiLoading,
      } = payload as IOnCompletedPayload;
      const pagesArray = Array.from(itemsMap.values());
      const maxPage = pagesArray[pagesArray.length - 1];
      setNumFound(numFound);
      setShouldTriggerGAFilterEvent(true);

      if (
        maxPage > 1 &&
        !direction &&
        state?.handlerParams?.currentPageNumber?.current === currentPage &&
        (loadedItems as Docs[]).every(
          ({ pid }) => pid && itemsMap.get(pid) === currentPage,
        ) &&
        state.numFound === numFound
      ) {
        return state;
      }

      if (itemsMap.size === 0) {
        return {
          ...state,
          handlerParams: {
            ...state.handlerParams,
            isUiLoading,
            itemsMap: new Map<string, number>(
              loadedItems
                ?.map((p) => [p?.pid, currentPage])
                .filter((tuple): tuple is [string, number] => tuple !== null),
            ),
          },
          filters,
          items: newItems,
          numFound,
        };
      }
      if (!shouldConcat) {
        return {
          ...state,
          handlerParams: {
            ...state.handlerParams,
            isUiLoading,
            itemsMap: new Map<string, number>(
              loadedItems
                ?.map((p) => [p?.pid, currentPage])
                .filter((tuple): tuple is [string, number] => tuple !== null),
            ),
          },
          filters,
          items: newItems,
          numFound,
        };
      } else {
        if (direction === "previous") {
          const derivedPageNumber = Array.from(itemsMap.values())[0];
          const prevMapItems = Array.from(itemsMap.entries());
          const newMapItems: [string, number][] = (loadedItems || [])
            .map((p) => [p?.pid, derivedPageNumber - 1])
            .filter((tuple): tuple is [string, number] => tuple !== null);
          return {
            ...state,
            handlerParams: {
              ...state.handlerParams,
              itemsMap: new Map<string, number>([...newMapItems, ...prevMapItems]),
              direction: "",
              isUiLoading,
            },
            filters,
            items: newItems,
            numFound,
          };
        } else if (direction === "next") {
          const prevMapItems = Array.from(itemsMap.entries());
          const newMapItems: [string, number][] = (loadedItems || [])
            .map((p) => [p?.pid, currentPage + 1])
            .filter((tuple): tuple is [string, number] => tuple !== null);

          return {
            ...state,
            handlerParams: {
              ...state.handlerParams,
              itemsMap: new Map<string, number>([...prevMapItems, ...newMapItems]),
              direction: "",
              isUiLoading,
            },
            filters,
            items: newItems,
            numFound,
          };
        } else {
          return {
            ...state,
            handlerParams: {
              ...state.handlerParams,
              isUiLoading,
              itemsMap: new Map<string, number>(
                (loadedItems || [])
                  .map((p) => [p?.pid, currentPage])
                  .filter((tuple): tuple is [string, number] => tuple !== null),
              ),
            },
            filters,
            items: newItems,
            numFound,
          };
        }
      }
    }
    case IUseRequestHandlerActionsKind.CLEAR_MAP:
      return {
        ...state,
        handlerParams: {
          ...state.handlerParams,
          itemsMap: new Map<string, number>(),
        },
      };
    case IUseRequestHandlerActionsKind.UPDATE_APPLIED_FILTER_ON_CLICK: {
      const { updateFunc, appliedFilter, params, GAdata } =
        payload as IUpdateFilterOnClick;
      updateFunc(params);
      return {
        ...state,
        handlerParams: {
          ...state.handlerParams,
          appliedFilters: {
            ...state.handlerParams.appliedFilters,
            [appliedFilter[0]]: appliedFilter[1],
          },
          appliedFilterOnClick: appliedFilter,
          GAdata,
        },
      };
    }
    case IUseRequestHandlerActionsKind.REMOVE_APPLIED_FILTER_ON_CLICK: {
      const { updateFunc, appliedFilter, params } = payload as IUpdateFilterOnClick;
      updateFunc(params);
      return {
        ...state,
        handlerParams: {
          ...state.handlerParams,
          appliedFilters: omit(
            {
              ...state.handlerParams.appliedFilters,
            },
            appliedFilter[0],
          ),
        },
      };
    }
    case IUseRequestHandlerActionsKind.CLEAR_APPLIED_FILTERS:
      return {
        ...state,
        handlerParams: {
          ...state.handlerParams,
          appliedFilters: null,
          isShopFilter: false,
        },
      };
    case IUseRequestHandlerActionsKind.UPDATE_SORTING: {
      const { updateFunc, sortingParam, params } = payload as IUpdateSorting;
      updateFunc({ ...params, activeSortingParam: sortingParam });
      return {
        ...state,
        handlerParams: {
          ...state.handlerParams,
          activeSortingParam: sortingParam,
        },
      };
    }
    case IUseRequestHandlerActionsKind.UPDATE_IS_SHOP_FILTER_SELECTED: {
      const { isShopFilter, selectedStore } = payload as IUpdateIsShopFilter;
      return {
        ...state,
        handlerParams: {
          ...state.handlerParams,
          isShopFilter,
          currentStore: selectedStore,
        },
      };
    }
    case IUseRequestHandlerActionsKind.FULL_OVERRIDE: {
      const { updateFunc, props, params } = payload as IFullOverridePayload;
      updateFunc({ ...params, ...props });
      const { activeSortingParam, appliedFilters, isShopFilter, isUiLoading } =
        props;
      return {
        ...state,
        handlerParams: {
          ...state.handlerParams,
          activeSortingParam: activeSortingParam,
          appliedFilters: appliedFilters,
          isShopFilter: isShopFilter,
          isUiLoading,
        },
      };
    }
    case IUseRequestHandlerActionsKind.SET_DIRECTION: {
      const { direction } = payload as IUpdateDirection;
      return {
        ...state,
        handlerParams: {
          ...state.handlerParams,
          direction,
        },
      };
    }
    default:
      return state;
  }
};
